import React, { useEffect, useState } from 'react';
import { useMutation } from '@apollo/client';
import { Result, Button, Spin, Card, Space } from 'antd';
import { Link, useParams } from 'react-router';
import { VERIFY_USER } from '../../graphql/mutations/mutations';
import { AuthCardLayout } from '../../layouts/AuthCardLayout';
import { SmileOutlined } from '@ant-design/icons';

interface VerificationState {
  status: 'success' | 'error' | 'info' | 'loading';
  title: string;
  subTitle: string;
  code?: string;
}

const VerifySignUpContent: React.FC = () => {
  const { token } = useParams();
  const [verificationState, setVerificationState] = useState<VerificationState>({
    status: 'loading',
    title: 'Verifying your email',
    subTitle: 'Please wait while we verify your email address...',
  });

  const [verifyUser] = useMutation(VERIFY_USER);

  useEffect(() => {
    if (!token) {
      setVerificationState({
        status: 'error',
        title: 'Invalid Verification Link',
        subTitle: 'The verification link is invalid or has expired.',
      });
      return;
    }

    verifyUser({ variables: { token } })
      .then(() => {
        setVerificationState({
          status: 'success',
          title: 'Email Verified Successfully!',
          subTitle: 'Your email has been verified. You can now log in to your account.',
        });
      })
      .catch(err => {
        const errorCode = err.graphQLErrors[0]?.extensions?.code;
        let errorState: VerificationState = {
          status: 'error',
          title: 'Verification Failed',
          subTitle: 'Unable to verify your email address.',
          code: errorCode,
        };

        switch (errorCode) {
          case 'TOKEN_EXPIRED':
            errorState.title = 'Link Expired';
            errorState.subTitle = 'The verification link has expired. Please request a new one.';
            break;
          case 'INVALID_TOKEN':
            errorState.title = 'Invalid Link';
            errorState.subTitle =
              'The verification link is invalid. Please check your email for the correct link.';
            break;
          case 'ALREADY_VERIFIED':
            errorState = {
              status: 'info',
              title: 'Already Verified',
              subTitle: 'Your email has already been verified. You can log in to your account.',
              code: errorCode,
            };
            break;
          default:
            // Use default error state
            break;
        }

        setVerificationState(errorState);
      });
  }, [token, verifyUser]);

  if (verificationState.status === 'loading') {
    return (
      <Result
        icon={<Spin size="large" />}
        title={verificationState.title}
        subTitle={verificationState.subTitle}
      />
    );
  }

  return (
    <Result
      status={verificationState.status}
      title={verificationState.title}
      subTitle={verificationState.subTitle}
      icon={verificationState.status === 'info' ? <SmileOutlined /> : undefined}
      extra={[
        <Space key="buttons">
          <Button type="primary" key="login">
            <Link to="/login">Go to Login</Link>
          </Button>
          {verificationState.status === 'error' &&
            verificationState.code !== 'ALREADY_VERIFIED' && (
              <Button key="resend">
                <Link to="/resend-signup-token">Resend confirmation email</Link>
              </Button>
            )}
        </Space>,
      ]}
    />
  );
};

export const VerifySignUp: React.FC = () => {
  return (
    <AuthCardLayout>
      <Card hoverable={false} style={{ width: '25rem', maxWidth: '90vw' }}>
        <VerifySignUpContent />
      </Card>
    </AuthCardLayout>
  );
};
